import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { ReportShell } from "@/features/reports/ReportShell";
import { KpiCard } from "@/features/reports/KpiCard";
import { useFar, useFinanceKpis } from "@/features/finance/api";
import { currentFy, fyOptions } from "@/features/finance/fy";
import { inr } from "@/features/finance/format";
import type { ReportFilters } from "@/features/reports/types";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";

export const Route = createFileRoute("/_authenticated/reports/fixed-asset-register")({
  component: FarReport,
});

function FarReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const [fy, setFy] = useState(currentFy());
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;
  const { data: rows = [], isLoading } = useFar(fy, effectiveBranchId);
  const { data: kpis } = useFinanceKpis(fy, effectiveBranchId);

  const exportRows = useMemo(() => () => rows.map((r) => ({
    Code: r.asset_code, Name: r.asset_name, Category: r.category_name ?? "",
    Department: r.department_name ?? "", Location: r.location_name ?? "",
    Status: r.status ?? "", Capitalisation: r.capitalisation_date ?? "",
    GrossBlock: r.gross_block, AccDep: r.accum_dep_closing ?? 0, NBV: r.nbv_closing ?? 0,
    DisposalDate: r.disposal_date ?? "", DisposalMode: r.disposal_mode ?? "",
    SaleValue: r.disposal_sale_value ?? "", GST: r.disposal_gst ?? "",
    GainLoss: r.disposal_gain_loss ?? "",
  })), [rows]);

  return (
    <ReportShell id="R-11" title="Fixed Asset Register (Finance)"
      description="Gross block, depreciation, NBV and disposal status per asset. Disposed assets remain visible as historical finance records."
      filters={filters} onChange={setFilters} enabled={["branchId", "categoryId"]}
      exportRows={exportRows}
      rightActions={
        <Select value={fy} onValueChange={setFy}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>{fyOptions().map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}</SelectContent>
        </Select>
      }>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <KpiCard label="Gross Block" value={inr(kpis?.gross_block ?? 0)} />
        <KpiCard label="Accumulated Dep." value={inr(kpis?.accumulated_dep ?? 0)} />
        <KpiCard label="NBV" value={inr(kpis?.nbv ?? 0)} tone="success" />
      </div>
      <Card><CardContent className="p-0 overflow-x-auto">
        {isLoading ? <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Gross Block</TableHead>
              <TableHead className="text-right">Acc. Dep.</TableHead>
              <TableHead className="text-right">NBV</TableHead>
              <TableHead>Disposal Date</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead className="text-right">Sale</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead className="text-right">Gain/Loss</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.slice(0, 200).map((r) => (
                <TableRow key={r.asset_id}>
                  <TableCell className="font-mono text-xs">{r.asset_code}</TableCell>
                  <TableCell className="font-medium">{r.asset_name}</TableCell>
                  <TableCell>{r.category_name ?? "—"}</TableCell>
                  <TableCell className="text-xs">{r.status ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(r.gross_block)}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(r.accum_dep_closing ?? 0)}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(r.nbv_closing ?? 0)}</TableCell>
                  <TableCell className="text-xs">{r.disposal_date ?? "—"}</TableCell>
                  <TableCell className="text-xs">{r.disposal_mode ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.disposal_sale_value != null ? inr(r.disposal_sale_value) : "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.disposal_gst != null ? inr(r.disposal_gst) : "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.disposal_gain_loss != null ? inr(r.disposal_gain_loss) : "—"}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={12} className="py-8 text-center text-sm text-muted-foreground">No assets capitalised yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
        <div className="border-t p-3 text-xs text-muted-foreground">{rows.length > 200 ? `Showing first 200 of ${rows.length} assets. Export for full list.` : `Showing ${rows.length} of ${rows.length} assets.`}</div>
      </CardContent></Card>
    </ReportShell>
  );
}
